import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import QuoteForm from '../../components/base/QuoteForm';
import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';

const CollectionsPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://danvanremovals.co.uk';
  
  useSEO({
    title: 'Collections & Deliveries Lancashire | Pickup Service | Dan & Van',
    description: 'Reliable collection and delivery service across Lancashire. Marketplace purchases, auction items, furniture. Same-day service available — 07530 166035',
    keywords: 'collection service Lancashire, delivery service, marketplace collection, auction pickup, furniture delivery, courier service',
    canonical: '/collections-deliveries',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Collections & Deliveries',
      provider: {
        '@type': 'MovingCompany',
        name: 'Dan & Van Removals',
        telephone: '07530166035',
        areaServed: 'Lancashire, UK',
      },
      description: 'Professional collection and delivery service across Lancashire. Perfect for marketplace purchases, auction items, gifts, or any situation where you need reliable transportation.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceRange: '££',
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden mt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/39008f678764948c546fd12dc5d82fd7.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-gold text-xs uppercase tracking-wider mb-4"
          >
            <Link to="/" className="hover:underline cursor-pointer">Home</Link> / Services / Collections & Deliveries
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-sans text-6xl font-bold mb-6"
          >
            Collections & Deliveries
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-white/90 mb-8"
          >
            Reliable pickup and delivery service across Lancashire
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Link
              to="/contact"
              className="inline-block px-10 py-4 bg-gold text-charcoal font-semibold text-lg rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="font-sans text-4xl font-bold text-charcoal mb-6">
                Professional collection and delivery service
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Need something collected from one location and delivered to another? Our flexible collection and delivery service is perfect for marketplace purchases, auction items, gifts, or any situation where you need reliable transportation.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                We handle everything from small parcels to large furniture items, providing the same level of care and professionalism as our full removal service.
              </p>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6 mt-12">
                What we can collect and deliver
              </h3>

              <ul className="space-y-4 mb-12">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Marketplace purchases:</strong> Facebook Marketplace, eBay, Gumtree items
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Auction items:</strong> Collection from auction houses and delivery to your home
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Furniture and appliances:</strong> New or second-hand items from retailers or private sellers
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Gifts and donations:</strong> Items being given to friends, family, or charities
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Business deliveries:</strong> Stock, equipment, or supplies for your business
                  </span>
                </li>
              </ul>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6">
                How our collection and delivery service works
              </h3>

              <div className="space-y-6 mb-12">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-gold rounded-full flex items-center justify-center mr-4">
                    <span className="font-sans text-xl font-bold text-charcoal">1</span>
                  </div>
                  <div>
                    <h4 className="font-sans text-xl font-bold text-charcoal mb-2">
                      Tell us what you need
                    </h4>
                    <p className="text-gray-700">
                      Provide details about the item, collection address, and delivery address
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-gold rounded-full flex items-center justify-center mr-4">
                    <span className="font-sans text-xl font-bold text-charcoal">2</span>
                  </div>
                  <div>
                    <h4 className="font-sans text-xl font-bold text-charcoal mb-2">
                      Get an instant quote
                    </h4>
                    <p className="text-gray-700">
                      We'll provide a clear, no-obligation quote based on your requirements
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-gold rounded-full flex items-center justify-center mr-4">
                    <span className="font-sans text-xl font-bold text-charcoal">3</span>
                  </div>
                  <div>
                    <h4 className="font-sans text-xl font-bold text-charcoal mb-2">
                      We collect and deliver
                    </h4>
                    <p className="text-gray-700">
                      Our team collects the item and delivers it safely to your specified location
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6">
                Why choose Dan & Van for collections and deliveries?
              </h3>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We understand that buying items online or from private sellers can be stressful. Will the seller be reliable? How will you get the item home? With our collection and delivery service, you can buy with confidence knowing we'll handle the logistics.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                We're fully insured, experienced in handling all types of items, and committed to providing a professional, reliable service every time.
              </p>

              <div className="bg-gray-50 border-l-4 border-gold p-6 rounded">
                <p className="text-lg text-charcoal font-medium mb-2">
                  Need something collected?
                </p>
                <p className="text-gray-700 mb-4">
                  Get in touch today for a free quote. We offer competitive rates and flexible scheduling.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:07530166035"
                    className="px-6 py-3 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    Call 07530 166035
                  </a>
                  <Link
                    to="/contact"
                    className="px-6 py-3 bg-gold text-charcoal font-semibold rounded hover:shadow-luxury-hover transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    Get a Quote
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-white border border-gold/20 rounded-lg p-8 shadow-luxury mb-8">
                  <h3 className="font-sans text-2xl font-bold text-charcoal mb-6">
                    Get Your Free Quote
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Contact us directly for a fast, free quote on your collection and delivery needs.
                  </p>
                  <div className="space-y-3">
                    <a
                      href="https://wa.me/447530166035"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full py-3 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line mr-2 text-xl"></i>
                      WhatsApp Us
                    </a>
                    <a
                      href="tel:07530166035"
                      className="flex items-center justify-center w-full py-3 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line mr-2 text-lg"></i>
                      Call Now
                    </a>
                  </div>
                </div>

                <div className="bg-charcoal text-white rounded-lg p-6">
                  <h4 className="font-sans font-bold mb-3">Coverage Area</h4>
                  <p className="text-sm text-gray-300 mb-4">
                    We provide collection and delivery services throughout Lancashire and surrounding areas.
                  </p>
                  <Link
                    to="/areas-we-cover"
                    className="text-gold hover:underline text-sm flex items-center cursor-pointer"
                  >
                    View all areas
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CollectionsPage;
